'use client';

import { Info, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../../lib/cn';

export interface CalcInput {
	/** Nome del valore (es. "Potenza impianto"). */
	label: string;
	/** Valore formattato mostrato accanto al nome. */
	value: string;
	/** Provenienza del valore: reale, da configurazione, da normativa o calcolato. */
	source: 'real' | 'config' | 'norm' | 'calc';
}

export interface CalcTraceData {
	/** Formula leggibile in testo semplice, es. "E = P × H / 1000". */
	formula: string;
	/** Valori usati nella formula, con relativa provenienza. */
	inputs: CalcInput[];
	/** Riferimento normativo testuale (es. articolo di legge/delibera). */
	norm: string;
	/** Nota facoltativa su semplificazioni o avvertenze del calcolo. */
	note?: string;
}

const SOURCE_LABEL: Record<CalcInput['source'], string> = {
	real: 'REALE',
	config: 'CONFIG',
	norm: 'NORMATIVA',
	calc: 'CALCOLATO',
};

const SOURCE_COLOR: Record<CalcInput['source'], string> = {
	real: 'bg-accent-green/15 text-accent-green border-accent-green/30',
	config: 'bg-accent/10 text-accent border-accent/20',
	norm: 'bg-accent-purple/10 text-accent-purple border-accent-purple/20',
	calc: 'bg-warn/10 text-warn border-warn/20',
};

export interface CalcTraceProps {
	/** Formula, valori e riferimento normativo da mostrare nel popover. */
	data: CalcTraceData;
	/** `compact` = piccola icona inline; `default` = icona + etichetta testuale. Default: `compact`. */
	variant?: 'compact' | 'default';
	/** Se `above`, forza il popover ad aprirsi sopra il bottone indipendentemente dallo spazio disponibile. Default: `auto`. */
	placement?: 'auto' | 'above';
}

const POPOVER_W = 288; // w-72
const POPOVER_H_EST = 360; // estimated max height for flip decision

/** Icona "i" che apre un popover con la formula, i valori usati e il riferimento normativo dietro un dato calcolato. */
export function CalcTrace({ data, variant = 'compact', placement = 'auto' }: CalcTraceProps) {
	const [open, setOpen] = useState(false);
	const [pos, setPos] = useState<{ top?: number; bottom?: number; left: number } | null>(null);
	const btnRef = useRef<HTMLButtonElement>(null);
	const popoverRef = useRef<HTMLDivElement>(null);

	const handleToggle = () => {
		if (!open && btnRef.current) {
			const rect = btnRef.current.getBoundingClientRect();
			// Horizontal: prefer aligning right edge to button right, fall back left
			const spaceRight = window.innerWidth - rect.right;
			const leftRaw =
				spaceRight >= POPOVER_W + 12 ? rect.right - POPOVER_W : rect.left - POPOVER_W + rect.width;
			const safeLeft = Math.max(8, Math.min(leftRaw, window.innerWidth - POPOVER_W - 8));
			// Vertical: use CSS bottom (anchored to button top) or top (anchored to button bottom)
			const spaceBelow = window.innerHeight - rect.bottom;
			if (placement === 'above' || spaceBelow < POPOVER_H_EST + 12) {
				// bottom of popover = top of button - gap
				setPos({ bottom: window.innerHeight - rect.top + 6, left: safeLeft });
			} else {
				setPos({ top: rect.bottom + 6, left: safeLeft });
			}
		}
		setOpen((v) => !v);
	};

	useEffect(() => {
		if (!open) return;
		const close = () => setOpen(false);
		const handleMouseDown = (e: MouseEvent) => {
			if (
				popoverRef.current &&
				!popoverRef.current.contains(e.target as Node) &&
				btnRef.current &&
				!btnRef.current.contains(e.target as Node)
			) {
				setOpen(false);
			}
		};
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				setOpen(false);
				btnRef.current?.focus();
			}
		};
		window.addEventListener('scroll', close, true);
		window.addEventListener('resize', close);
		document.addEventListener('mousedown', handleMouseDown);
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			window.removeEventListener('scroll', close, true);
			window.removeEventListener('resize', close);
			document.removeEventListener('mousedown', handleMouseDown);
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [open]);

	return (
		<>
			<button
				ref={btnRef}
				type="button"
				onClick={handleToggle}
				title="Come viene calcolato"
				aria-label="Come viene calcolato"
				aria-haspopup="dialog"
				aria-expanded={open}
				className={cn(
					'tap-target flex items-center justify-center gap-1 rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
					open ? 'text-accent' : 'text-text-muted hover:text-accent',
					variant === 'default' && 'text-[11px] font-semibold',
				)}
			>
				<Info className="h-3 w-3" />
				{variant === 'default' && <span>Come calcolato</span>}
			</button>

			{open &&
				pos &&
				createPortal(
					<div
						ref={popoverRef}
						// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- manually-positioned portal popover, not a top-layer native <dialog>; role="dialog" + aria-label is correct here
						role="dialog"
						aria-label="Metodologia di calcolo"
						style={{
							position: 'fixed',
							top: pos.top,
							bottom: pos.bottom,
							left: pos.left,
							width: POPOVER_W,
							maxHeight: 'calc(100vh - 16px)',
							overflowY: 'auto',
							zIndex: 9999,
						}}
						className="rounded-xl border border-border bg-panel shadow-2xl"
					>
						<div className="flex items-center justify-between border-b border-border px-3 py-2">
							<span className="text-[12px] font-bold uppercase tracking-wider text-text-muted">
								Metodologia di calcolo
							</span>
							<button
								type="button"
								onClick={() => setOpen(false)}
								aria-label="Chiudi"
								className="rounded text-text-muted hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
							>
								<X className="h-3 w-3" />
							</button>
						</div>

						<div className="space-y-3 p-3">
							<div>
								<div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-text-muted">
									Formula
								</div>
								<div className="whitespace-pre-wrap rounded-lg border border-border bg-background px-3 py-2 font-mono text-[12px] leading-relaxed text-accent">
									{data.formula}
								</div>
							</div>

							{data.inputs.length > 0 && (
								<div>
									<div className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-text-muted">
										Valori usati
									</div>
									<div className="space-y-1">
										{data.inputs.map((inp) => (
											<div
												key={inp.label}
												className="flex items-center justify-between gap-2 text-[12px]"
											>
												<span className="text-text-muted">{inp.label}</span>
												<div className="flex items-center gap-1.5">
													<span className="font-mono font-semibold text-text">{inp.value}</span>
													<span
														className={cn(
															'rounded border px-1 py-px text-[11px] font-bold uppercase tracking-wide',
															SOURCE_COLOR[inp.source],
														)}
													>
														{SOURCE_LABEL[inp.source]}
													</span>
												</div>
											</div>
										))}
									</div>
								</div>
							)}

							<div className="rounded-lg border border-accent-purple/20 bg-accent-purple/[0.06] px-2.5 py-1.5 text-[11px] text-accent-purple">
								<span className="font-bold">Riferimento normativo: </span>
								{data.norm}
							</div>

							{data.note && (
								<div className="rounded-lg border border-warn/20 bg-warn/[0.06] px-2.5 py-1.5 text-[11px] text-warn">
									<span className="font-bold">Nota: </span>
									{data.note}
								</div>
							)}
						</div>
					</div>,
					document.body,
				)}
		</>
	);
}
